import { parseParams } from "../../utils";
import { extensionForMimeType } from "../../utils/speechUtils";
import callApi from "../../utils/api";
import {
  AI_PLANET_PROXY,
  ASSESSMENT_QUEUE_SERVICE,
  API_BASE_URL,
  json_files,
} from "../../utils/constants";

const irApi = callApi(API_BASE_URL);
const api = callApi(AI_PLANET_PROXY || "");
const queueService = callApi(ASSESSMENT_QUEUE_SERVICE);

const speechOptionsConstant = {
  geography: {
    urban: "Male",
    rural: "Female",
  },
  learningGeneration: {
    1: 60,
    2: 80,
    3: 90,
  },
  accent: {
    urban: "US",
    rural: "IN",
  },
};

// Configuration for how many Words per Second to be read during speech evaluation
export const speechEvaluationDuration = {
  urban: 1.25,
  rural: 0.75,
};

const defaultSpeechConfig = {
  accent: "EN_IN",
  gender: "Female",
  words_per_minute: 80,
};

const TEXT_TO_SPEECH = "/text-to-speech";
const SPEECH_EVALUATION = "/evaluate-speech";
const ADD_SPEECH_EVALUATION_HISTORY =
  "/api/intelliread/course/{{courseId}}/speech-evaluation-history";

const IR_LEVEL_MAPPINGS = {
  // production: 'https://azvasa-lms-cdn-dev.s3.ap-south-1.amazonaws.com/IR-SERVICE/grades/ir-level-grade-mappings.json',
  production:
    "https://s3.ap-south-1.amazonaws.com/azvasa-lms-cdn-dev-new/IR-SERVICE/configuration/irGrade.json",
  development:
    "https://s3.ap-south-1.amazonaws.com/azvasa-lms-cdn-dev-new/IR-SERVICE/configuration/irGrade.json",
};

const GET_EVALUATED_ASSESSMENT =
  "/v1/api/assessments/evaluation/{{evaluationId}}";
const EVALUATE_ASSESSMENT = "/v1/api/assessments/evaluate";

const ASSESSMENT_ANSWERS_ACTIVITY = "/v1/api/master/assessments/answers";
const GET_ASSESSMENT_ANSWERS_ACTIVITY =
  "/v1/api/master/assessments/{{assessmentId}}/answers";

export const IR_LEVEL_MAPPING =
  process.env.NODE_ENV === "development"
    ? IR_LEVEL_MAPPINGS.development
    : IR_LEVEL_MAPPINGS.production;
export const IR_COURSE_MAPPING =
  "https://s3.ap-south-1.amazonaws.com/azvasa-lms-cdn-dev-new/IR-SERVICE/configuration/ir-levels-course-assignments.json";
// export const IR_COURSE_MAPPING = 'http://localhost:4000/assets/ir-levels-course-assignments.json';

export const textToSpeech = async (payload) => {
  // If there's no text payload or window is not available (SSR), call directly.
  const hasWindow = typeof window !== "undefined" && window;
  const text = payload && payload.text ? String(payload.text) : null;

  if (!text || !hasWindow) {
    return await api.post(TEXT_TO_SPEECH, {
      ...defaultSpeechConfig,
      ...payload,
    });
  }

  try {
    // Use a process-wide map to avoid duplicate concurrent TTS requests for same text
    window.__ttsPromises = window.__ttsPromises || new Map();

    if (window.__ttsPromises.has(text)) {
      // There's already a pending request for this exact text; reuse it
      return await window.__ttsPromises.get(text);
    }

    // Create the request promise and store it so other callers can await the same promise
    const p = api
      .post(TEXT_TO_SPEECH, { ...defaultSpeechConfig, ...payload })
      .then((data) => {
        try {
          if (data && data.response && data.response.audio) {
            // Normalize S3 endpoint if required (keeps behavior consistent with other callers)
            const correctedAudioUrl = data.response.audio.replace(
              "knowledgebasechat.s3.eu-west-1.amazonaws.com",
              "knowledgebasechat.s3-us-west-1.amazonaws.com",
            );

            // Mutate response to return corrected URL
            data.response.audio = correctedAudioUrl;
          }
        } catch (e) {
          // swallow transformation errors but still return the response
        }
        return data;
      })
      .finally(() => {
        try {
          window.__ttsPromises.delete(text);
        } catch (e) {
          // ignore
        }
      });

    window.__ttsPromises.set(text, p);

    return await p;
  } catch (err) {
    // Fallback: if anything goes wrong, perform a direct request
    return await api.post(TEXT_TO_SPEECH, {
      ...defaultSpeechConfig,
      ...payload,
    });
  }
};
export const evaluateSpeech = async (payload) => {
  return await api.post(SPEECH_EVALUATION, payload, {}, true);
};

export const addSpeechEvaluationHistory = async (payload) => {
  let { body, params, audio } = payload;
  const formData = new FormData();

  Object.keys(body).forEach((key) => {
    formData.append(key, body[key]);
  });

  // Extension must match the recorded container — this endpoint shares the
  // upload filter with /evaluate-speech, and a browser never records WAV.
  formData.append(
    "student_answer",
    audio,
    `student_answer${extensionForMimeType(audio?.type)}`,
  );

  return await api.post(
    parseParams(ADD_SPEECH_EVALUATION_HISTORY, params),
    formData,
    {},
    true,
  );
};

let _speechConfigDataPromise = null;

export const getSpeechConfig = async (geography, learningGeneration) => {
  // Fetch the S3 config once per session and reuse; dedupe concurrent callers
  if (!_speechConfigDataPromise) {
    _speechConfigDataPromise = fetch(json_files.speechConfig.url)
      .then((response) => response.json())
      .catch((err) => {
        // Reset on failure so the next caller can retry
        _speechConfigDataPromise = null;
        throw err;
      });
  }

  const data = await _speechConfigDataPromise;

  geography = String(geography).toLowerCase();
  if (!geography || geography === "null") {
    geography = "urban";
  }

  if (!learningGeneration && "number" !== typeof learningGeneration) {
    learningGeneration = 2;
  }

  let conf = {};
  data.map((item) => {
    if (item.geographicalType.toLowerCase() !== geography) return;

    const learningGenerations = item.learningGeneration.filter(
      (gen) => gen.generation == learningGeneration,
    );

    conf.accent = learningGenerations[0].accent;
    conf.gender = learningGenerations[0].gender;
    conf.words_per_minute = learningGenerations[0].words_per_minute;

    return item;
  });

  return conf;
};

export const queueAssessmentForEvaluation = async ({
  body = { assessmentId, courseId, sectionId, chapterId, assessmentAnswers },
}) => {
  return queueService.post(EVALUATE_ASSESSMENT, body);
};

export const getAssessmentEvaluationResult = async (evaluationId) =>
  queueService.get(parseParams(GET_EVALUATED_ASSESSMENT, { evaluationId }));

export const saveAssessmentAnswersActivity = async (assessmentId, answers) =>
  await irApi.post(ASSESSMENT_ANSWERS_ACTIVITY, {
    assessmentId,
    selectedAnswers: answers,
  });

export const getAssessmentAnswersActivity = async (assessmentId) =>
  await irApi.get(
    parseParams(GET_ASSESSMENT_ANSWERS_ACTIVITY, { assessmentId }),
  );

// Clears the server-saved answer set for one assessment (the
// network-resilience cache). Called when a session retake starts so the
// previous attempt's answers don't get prefetched into the new attempt.
export const deleteAssessmentAnswersActivity = async (assessmentId) =>
  await irApi.delete(
    parseParams(GET_ASSESSMENT_ANSWERS_ACTIVITY, { assessmentId }),
  );
